// ReportingPage — risk heatmap reporting.
// Renders one collapsible RiskGrid per appetite definition, populated with risks
// assigned to that appetite (most-specific taxonomy path match).
// Numbered dots on the grid correspond to rows in the risk table legend.
// Table size is user-configurable (5/10/15/20).
// Left sidebar: taxonomy tree + register tree for filtering by classification and register hierarchy.

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { reportingApi } from '@/lib/api/reporting'
import type { HeatmapGroup, HeatmapRisk } from '@/lib/api/reporting'
import { scoringApi } from '@/lib/api/scoring'
import type { TaxonomyNode } from '@/lib/api/scoring'
import { registersApi, buildRegisterTree } from '@/lib/api/registers'
import type { Register, RegisterTreeNode } from '@/lib/api/registers'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import {
  RiskGrid,
  RiskHeatmapDots,
  appetiteKey,
  exportHeatmapGridSvg,
  exportHeatmapFullSvg,
  downloadSvg,
} from '@/components/features/risk-grid'
import type { GridConfig, AppetiteMap, PlottedRisk, HeatmapExportParams } from '@/components/features/risk-grid'
import {
  BarChart3,
  Info,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Download,
  FolderOpen,
  Folder,
  LayoutGrid,
  Network,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const TABLE_SIZE_OPTIONS = [5, 10, 15, 20] as const

export function RiskHeatmapPage() {
  const { slug } = useTenant()
  const api = useApi()
  const rptApi = reportingApi(api)
  const navigate = useNavigate()
  const [tableSize, setTableSize] = useState<number>(10)
  const [excludeChildren, setExcludeChildren] = useState(true)
  const [staleTaxonomyMode, setStaleTaxonomyMode] = useState<'include' | 'exclude' | 'only'>('include')
  const [taxonomyNodeId, setTaxonomyNodeId] = useState<string>('')
  const [taxonomyNodeName, setTaxonomyNodeName] = useState<string>('')
  const [registerNodeId, setRegisterNodeId] = useState<string>('')
  const [registerNodeName, setRegisterNodeName] = useState<string>('')
  const [registerNodePath, setRegisterNodePath] = useState<string>('')
  const [registerDepth, setRegisterDepth] = useState<number>(-1)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const filters = {
    excludeChildren: excludeChildren || undefined,
    taxonomyNodeId: taxonomyNodeId || undefined,
  }

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.reporting.riskHeatmap(slug, filters),
    queryFn: () => rptApi.riskHeatmap({
      exclude_children: excludeChildren || undefined,
      taxonomy_node_id: taxonomyNodeId || undefined,
    }),
  })

  // Fetch register tree for sidebar + filtering
  const regApi = registersApi(api)
  const { data: registerData } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => regApi.tree(),
  })

  // Build a lookup from register ID → materialized path for client-side filtering.
  // Risks carry register_id (UUID) but not the path, so we need this map to
  // resolve each risk's register path and compare it against the selected node.
  const registerPathMap: Map<string, string> | null = registerData
    ? new Map(registerData.map((r) => [r.id, r.path]))
    : null

  // registerFilterFn: returns true if a risk's register matches the selected
  // node + depth constraint. Used as a predicate in applyClientFilters.
  // When no register is selected, this is null (no register filtering).
  // Note: root register has path '' (empty string) which is a valid selection.
  // We use registerNodeId (not registerNodePath) as the null guard since path
  // can legitimately be empty.
  // Paths use leading-dot notation: root='', children='.001', grandchildren='.001.002'.
  // Split('.') on '.001' gives ['','001'] (length 2), so we subtract 1 for the leading dot.
  const selectedPathSegments = registerNodePath === '' ? 0 : registerNodePath.split('.').length - 1
  const registerFilterFn: ((registerID: string) => boolean) | null =
    registerNodeId && registerPathMap
      ? (riskRegisterID: string) => {
          const riskPath = registerPathMap.get(riskRegisterID)
          if (riskPath === undefined) return false
          // Exact match: risk is directly in the selected register
          if (riskPath === registerNodePath) return true
          // Descendant check: for root (''), every non-empty path is a descendant.
          // For non-root, path must start with selectedPath + '.'
          const isDescendant = registerNodePath === ''
            ? riskPath.length > 0
            : riskPath.startsWith(registerNodePath + '.')
          if (!isDescendant) return false
          // Unlimited depth: include all descendants
          if (registerDepth < 0) return true
          // Count how many levels deeper the risk's register is
          const riskSegments = riskPath === '' ? 0 : riskPath.split('.').length - 1
          const depthDiff = riskSegments - selectedPathSegments
          return depthDiff <= registerDepth
        }
      : null

  function handleSelectTaxonomy(nodeId: string, nodeName: string) {
    setTaxonomyNodeId(nodeId)
    setTaxonomyNodeName(nodeName)
  }

  function handleClearTaxonomy() {
    setTaxonomyNodeId('')
    setTaxonomyNodeName('')
  }

  function handleSelectRegister(nodeId: string, nodeName: string, nodePath: string) {
    setRegisterNodeId(nodeId)
    setRegisterNodeName(nodeName)
    setRegisterNodePath(nodePath)
  }

  function handleClearRegister() {
    setRegisterNodeId('')
    setRegisterNodeName('')
    setRegisterNodePath('')
  }

  const filterParts: string[] = []
  if (taxonomyNodeName) filterParts.push(`[Taxonomy: ${taxonomyNodeName}]`)
  if (registerNodeName) {
    const depthLabel = registerDepth < 0 ? 'All depths' : registerDepth === 0 ? 'Selected only' : `Depth ${registerDepth}`
    filterParts.push(`[Register: ${registerNodeName} · ${depthLabel}]`)
  }
  if (excludeChildren) filterParts.push('[Excl. child risks]')
  if (staleTaxonomyMode === 'exclude') filterParts.push('[Excl. stale taxonomy]')
  else if (staleTaxonomyMode === 'only') filterParts.push('[Stale taxonomy only]')
  const filterLabel = filterParts.length > 0 ? filterParts.join('  ') : undefined

  function handleRiskClick(riskId: string) {
    navigate({ to: `/t/${slug}/risks/${riskId}` })
  }

  function toggleSection(appetiteId: string) {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(appetiteId)) {
        next.delete(appetiteId)
      } else {
        next.add(appetiteId)
      }
      return next
    })
  }

  // Three-column layout: nav sidebar (RootLayout) | filter sidebar | heatmap content.
  // We break out of <main>'s p-6 padding AND the breadcrumb bar above it using negative
  // margins, then render breadcrumbs only above the heatmap column.
  // RootLayout structure: breadcrumb bar (h-9 = py-2 + text) then <main class="p-6">.
  // So we need -mt-[calc(theme(spacing.6)+2.25rem)] to escape padding + breadcrumb height,
  // and -mx-6 -mb-6 to escape horizontal and bottom padding.
  return (
    <div className="relative z-10 -mx-6 -mb-6 -mt-[calc(1.5rem+2.25rem)] flex h-[calc(100vh-8.25rem)] bg-background">
      {/* Filter sidebar — fixed width, full height, vertically split */}
      {/* pt matches the breadcrumb bar height in the heatmap column so content aligns */}
      <aside className="flex w-60 flex-shrink-0 flex-col border-r bg-background pt-[2.25rem]">
        <div className="min-h-0 flex-1 border-b">
          <TaxonomySidebar
            selectedNodeId={taxonomyNodeId}
            onSelectNode={handleSelectTaxonomy}
            onClearFilter={handleClearTaxonomy}
          />
        </div>
        <div className="min-h-0 flex-1">
          <RegisterSidebar
            registers={registerData ?? []}
            selectedNodeId={registerNodeId}
            onSelectNode={handleSelectRegister}
            onClearFilter={handleClearRegister}
          />
        </div>
      </aside>

      {/* Heatmap column: breadcrumbs + scrollable content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Breadcrumb bar — mirrors RootLayout's breadcrumb styling */}
        <div className="flex items-center border-b px-6 py-2">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
            <span className="text-muted-foreground">Reporting</span>
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium text-foreground">Risk Heatmap</span>
          </nav>
        </div>

        {/* Scrollable heatmap content */}
        <div className="flex-1 overflow-y-auto">
          <HeatmapContent
            data={data}
            error={error}
            isLoading={isLoading}
            refetch={refetch}
            tableSize={tableSize}
            setTableSize={setTableSize}
            excludeChildren={excludeChildren}
            setExcludeChildren={setExcludeChildren}
            staleTaxonomyMode={staleTaxonomyMode}
            setStaleTaxonomyMode={setStaleTaxonomyMode}
            taxonomyNodeName={taxonomyNodeName}
            registerNodeName={registerNodeName}
            registerFilterFn={registerFilterFn}
            registerNodeId={registerNodeId}
            registerDepth={registerDepth}
            setRegisterDepth={setRegisterDepth}
            filterLabel={filterLabel}
            openSections={openSections}
            toggleSection={toggleSection}
            onRiskClick={handleRiskClick}
          />
        </div>
      </div>
    </div>
  )
}

// --- Taxonomy tree sidebar ---

interface TaxonomySidebarProps {
  selectedNodeId: string
  onSelectNode: (nodeId: string, nodeName: string) => void
  onClearFilter: () => void
}

function TaxonomySidebar({ selectedNodeId, onSelectNode, onClearFilter }: TaxonomySidebarProps) {
  const api = useApi()
  const { slug } = useTenant()
  const scrApi = scoringApi(api)

  const { data: taxonomy, error, isLoading } = useQuery({
    queryKey: queryKeys.scoring.taxonomyNodes(slug),
    queryFn: () => scrApi.getTaxonomyNodes(),
  })

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const hasAutoExpanded = useRef(false)

  const tree = taxonomy?.nodes ? buildTaxonomyTree(taxonomy.nodes) : []

  useEffect(() => {
    if (tree.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true
      // Auto-expand root-level nodes
      setExpandedIds(new Set(tree.map((n) => n.id)))
      // Auto-select first root node if nothing selected yet
      if (!selectedNodeId && tree.length === 1) {
        onSelectNode(tree[0]!.id, tree[0]!.name)
      }
    }
  }, [taxonomy]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <div className="flex h-full flex-col">
      <div className="border-b p-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Taxonomy Filter
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="space-y-2 p-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-7 animate-pulse rounded bg-muted/50" />
            ))}
          </div>
        )}

        {error && (
          <div className="p-3">
            <ErrorState
              message="Failed to load taxonomy"
              technicalDetail={(error as Error).message}
            />
          </div>
        )}

        {!isLoading && !error && (
          <div className="py-1">
            {/* "All Categories" option */}
            <div
              className={cn(
                'mx-1 flex cursor-pointer items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm',
                'transition-colors hover:bg-accent/50',
                !selectedNodeId && 'bg-accent font-semibold text-accent-foreground',
              )}
              style={{ paddingLeft: 8 }}
              onClick={onClearFilter}
            >
              <LayoutGrid className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">All Categories</span>
            </div>

            {/* Tree nodes */}
            {tree.map((node) => (
              <TaxonomyTreeNode
                key={node.id}
                node={node}
                depth={0}
                expandedIds={expandedIds}
                selectedId={selectedNodeId}
                onToggle={toggleExpand}
                onSelect={onSelectNode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Taxonomy tree node ---

interface TaxTreeNode {
  id: string
  name: string
  description?: string | null
  depth: number
  children: TaxTreeNode[]
}

interface TaxonomyTreeNodeProps {
  node: TaxTreeNode
  depth: number
  expandedIds: Set<string>
  selectedId: string
  onToggle: (id: string) => void
  onSelect: (id: string, name: string) => void
}

function TaxonomyTreeNode({
  node,
  depth,
  expandedIds,
  selectedId,
  onToggle,
  onSelect,
}: TaxonomyTreeNodeProps) {
  const isExpanded = expandedIds.has(node.id)
  const isSelected = node.id === selectedId
  const hasChildren = node.children.length > 0
  const paddingLeft = 8 + (depth + 1) * 16 // +1 to indent past "All Categories"

  return (
    <div>
      <div
        className={cn(
          'group mx-1 flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-sm',
          'transition-colors hover:bg-accent/50',
          isSelected && 'bg-accent font-semibold text-accent-foreground',
        )}
        style={{ paddingLeft }}
        onClick={() => onSelect(node.id, node.name)}
      >
        {/* Expand/collapse toggle */}
        <button
          className={cn(
            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm',
            'hover:bg-accent',
            !hasChildren && 'invisible',
          )}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(node.id)
          }}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            ))}
        </button>

        {/* Folder icon */}
        {isExpanded && hasChildren ? (
          <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <Folder className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}

        {/* Label */}
        <span className="flex-1 truncate" title={node.name}>
          {node.name}
        </span>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TaxonomyTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              selectedId={selectedId}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// --- Register tree sidebar ---

interface RegisterSidebarProps {
  registers: Register[]
  selectedNodeId: string
  onSelectNode: (nodeId: string, nodeName: string, nodePath: string) => void
  onClearFilter: () => void
}

function RegisterSidebar({ registers, selectedNodeId, onSelectNode, onClearFilter }: RegisterSidebarProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const hasAutoExpanded = useRef(false)

  const tree = buildRegisterTree(registers)

  useEffect(() => {
    if (tree.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true
      setExpandedIds(new Set(tree.map((n) => n.id)))
      // Auto-select root register if nothing selected yet
      if (!selectedNodeId && tree.length === 1) {
        onSelectNode(tree[0]!.id, tree[0]!.name, tree[0]!.path)
      }
    }
  }, [registers]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <div className="flex h-full flex-col">
      <div className="border-b p-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Register Filter
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {registers.length === 0 && (
          <div className="space-y-2 p-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-7 animate-pulse rounded bg-muted/50" />
            ))}
          </div>
        )}

        {registers.length > 0 && (
          <div className="py-1">
            {/* "All Registers" option */}
            <div
              className={cn(
                'mx-1 flex cursor-pointer items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm',
                'transition-colors hover:bg-accent/50',
                !selectedNodeId && 'bg-accent font-semibold text-accent-foreground',
              )}
              style={{ paddingLeft: 8 }}
              onClick={onClearFilter}
            >
              <Network className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">All Registers</span>
            </div>

            {tree.map((node) => (
              <RegisterTreeNodeRow
                key={node.id}
                node={node}
                depth={0}
                expandedIds={expandedIds}
                selectedId={selectedNodeId}
                onToggle={toggleExpand}
                onSelect={onSelectNode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Register tree node ---

interface RegisterTreeNodeRowProps {
  node: RegisterTreeNode
  depth: number
  expandedIds: Set<string>
  selectedId: string
  onToggle: (id: string) => void
  onSelect: (id: string, name: string, path: string) => void
}

function RegisterTreeNodeRow({
  node,
  depth,
  expandedIds,
  selectedId,
  onToggle,
  onSelect,
}: RegisterTreeNodeRowProps) {
  const isExpanded = expandedIds.has(node.id)
  const isSelected = node.id === selectedId
  const hasChildren = node.children.length > 0
  const paddingLeft = 8 + (depth + 1) * 16

  return (
    <div>
      <div
        className={cn(
          'group mx-1 flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-sm',
          'transition-colors hover:bg-accent/50',
          isSelected && 'bg-accent font-semibold text-accent-foreground',
        )}
        style={{ paddingLeft }}
        onClick={() => onSelect(node.id, node.name, node.path)}
      >
        <button
          className={cn(
            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm',
            'hover:bg-accent',
            !hasChildren && 'invisible',
          )}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(node.id)
          }}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            ))}
        </button>

        {isExpanded && hasChildren ? (
          <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <Folder className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}

        <span className="flex-1 truncate" title={node.name}>
          {node.name}
        </span>
      </div>

      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <RegisterTreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              selectedId={selectedId}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// --- Build taxonomy tree from flat node list ---

function buildTaxonomyTree(nodes: TaxonomyNode[]): TaxTreeNode[] {
  const nodeMap = new Map<string, TaxTreeNode>()
  const roots: TaxTreeNode[] = []

  // Sort by path to ensure parents come before children
  const sorted = [...nodes].sort((a, b) => a.path.localeCompare(b.path))

  for (const n of sorted) {
    const treeNode: TaxTreeNode = {
      id: n.id,
      name: n.name,
      description: n.description,
      depth: n.depth,
      children: [],
    }
    nodeMap.set(n.id, treeNode)

    if (n.parent_id && nodeMap.has(n.parent_id)) {
      nodeMap.get(n.parent_id)!.children.push(treeNode)
    } else {
      roots.push(treeNode)
    }
  }

  return roots
}

// --- Heatmap content (right panel) ---

interface HeatmapContentProps {
  data: Awaited<ReturnType<ReturnType<typeof reportingApi>['riskHeatmap']>> | undefined
  error: Error | null
  isLoading: boolean
  refetch: () => void
  tableSize: number
  setTableSize: (n: number) => void
  excludeChildren: boolean
  setExcludeChildren: (v: boolean) => void
  staleTaxonomyMode: 'include' | 'exclude' | 'only'
  setStaleTaxonomyMode: (v: 'include' | 'exclude' | 'only') => void
  taxonomyNodeName: string
  registerNodeName: string
  registerFilterFn: ((registerID: string) => boolean) | null
  registerNodeId: string
  registerDepth: number
  setRegisterDepth: (v: number) => void
  filterLabel?: string
  openSections: Set<string>
  toggleSection: (id: string) => void
  onRiskClick: (riskId: string) => void
}

function HeatmapContent({
  data,
  error,
  isLoading,
  refetch,
  tableSize,
  setTableSize,
  excludeChildren,
  setExcludeChildren,
  staleTaxonomyMode,
  setStaleTaxonomyMode,
  taxonomyNodeName,
  registerNodeName,
  registerFilterFn,
  registerNodeId,
  registerDepth,
  setRegisterDepth,
  filterLabel,
  openSections,
  toggleSection,
  onRiskClick,
}: HeatmapContentProps) {
  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load risk heatmap"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!data) return null

  const { groups: rawGroups, grid } = data

  const gridConfig: GridConfig = {
    likelihoodLevels: grid.likelihood_levels.map((l) => ({ value: l.value, label: l.label })),
    impactLevels: grid.impact_levels.map((l) => ({ value: l.value, label: l.label })),
  }

  // Apply client-side filters: stale taxonomy + register
  function applyClientFilters(risks: HeatmapRisk[]): HeatmapRisk[] {
    let result = risks
    // Stale taxonomy filter
    if (staleTaxonomyMode === 'exclude') result = result.filter((r) => !r.stale_taxonomy)
    else if (staleTaxonomyMode === 'only') result = result.filter((r) => r.stale_taxonomy)
    // Register filter (path prefix + depth via filter function)
    if (registerFilterFn) result = result.filter((r) => registerFilterFn(r.register_id))
    return result
  }

  const needsFiltering = staleTaxonomyMode !== 'include' || registerFilterFn !== null
  const groups = !needsFiltering
    ? rawGroups
    : rawGroups
        .map((g) => {
          const filtered = applyClientFilters(g.risks)
          return { ...g, risks: filtered, total_risk_count: filtered.length }
        })
        .filter((g) => g.risks.length > 0)

  // Count stale risks across all groups for the label
  const staleCount = rawGroups.reduce(
    (sum, g) => sum + g.risks.filter((r) => r.stale_taxonomy).length,
    0,
  )

  const hasData = groups.some((g) => g.risks.length > 0)

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Risk Heatmaps by Appetite</h1>
          <p className="text-sm text-muted-foreground">
            Residual risk positions grouped by risk appetite definition.
            {(taxonomyNodeName || registerNodeName) && (
              <span className="font-medium text-foreground">
                {' '}Showing:{' '}
                {[taxonomyNodeName && `[Taxonomy: ${taxonomyNodeName}]`, registerNodeName && `[Register: ${registerNodeName}]`]
                  .filter(Boolean)
                  .join('  ')}
              </span>
            )}
          </p>
        </div>
        {hasData && (
          <div className="flex shrink-0 items-center gap-2">
            <label htmlFor="table-size" className="text-sm text-muted-foreground">
              Show top
            </label>
            <select
              id="table-size"
              value={tableSize}
              onChange={(e) => setTableSize(Number(e.target.value))}
              className="w-20 rounded-md border bg-background px-2 py-1 text-sm"
            >
              {TABLE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground">risks</span>
          </div>
        )}
      </div>

      {/* Filter controls */}
      <div className="flex flex-wrap items-center gap-4 rounded-md border bg-muted/30 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <input
            id="exclude-children"
            type="checkbox"
            checked={excludeChildren}
            onChange={(e) => setExcludeChildren(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="exclude-children" className="text-sm">
            Exclude child risks
          </label>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="stale-taxonomy" className="text-sm text-muted-foreground">
            Stale taxonomy:
          </label>
          <select
            id="stale-taxonomy"
            value={staleTaxonomyMode}
            onChange={(e) => setStaleTaxonomyMode(e.target.value as 'include' | 'exclude' | 'only')}
            className="rounded-md border bg-background px-2 py-1 text-sm"
          >
            <option value="include">Include{staleCount > 0 ? ` (${staleCount})` : ''}</option>
            <option value="exclude">Exclude{staleCount > 0 ? ` (${staleCount})` : ''}</option>
            <option value="only">Only show{staleCount > 0 ? ` (${staleCount})` : ''}</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="register-depth" className="text-sm text-muted-foreground">
            Register depth:
          </label>
          <select
            id="register-depth"
            value={registerDepth}
            onChange={(e) => setRegisterDepth(Number(e.target.value))}
            disabled={!registerNodeId}
            className="rounded-md border bg-background px-2 py-1 text-sm disabled:opacity-50"
          >
            <option value={0}>Selected register only</option>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={-1}>All</option>
          </select>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium">No appetite definitions configured</p>
            <p className="text-xs">
              Risk appetite must be defined in Framework Settings before heatmaps can be displayed.
            </p>
          </div>
        </div>
      )}

      {!hasData && groups.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>
            No scored risks found. Risks must be in an active lifecycle state with residual
            likelihood and impact assessed to appear on the heatmap.
          </span>
        </div>
      )}

      {groups.map((group) => (
        <HeatmapSection
          key={group.appetite_id}
          group={group}
          gridConfig={gridConfig}
          tableSize={tableSize}
          onRiskClick={onRiskClick}
          open={openSections.has(group.appetite_id)}
          onToggle={() => toggleSection(group.appetite_id)}
          filterLabel={filterLabel}
        />
      ))}
    </div>
  )
}

// --- Collapsible per-appetite heatmap section ---

interface HeatmapSectionProps {
  group: HeatmapGroup
  gridConfig: GridConfig
  tableSize: number
  onRiskClick: (riskId: string) => void
  open: boolean
  onToggle: () => void
  filterLabel?: string
}

function HeatmapSection({ group, gridConfig, tableSize, onRiskClick, open, onToggle, filterLabel }: HeatmapSectionProps) {

  const outOfAppetiteCount = group.risks.filter((r) => r.outside_appetite).length
  const isTruncated = group.total_risk_count > group.risks.length

  // Build appetite map (used by both rendering and export)
  const appetite: AppetiteMap = {}
  for (const cell of group.permitted_cells) {
    appetite[appetiteKey(cell.likelihood_level, cell.impact_level)] = true
  }

  function getExportParams(): HeatmapExportParams {
    return {
      title: group.taxonomy_node_name,
      breadcrumb: group.taxonomy_breadcrumb,
      config: gridConfig,
      appetite,
      risks: group.risks,
      tableSize,
      totalRiskCount: group.total_risk_count,
      outOfAppetiteCount,
      filterLabel,
    }
  }

  function handleDownloadGrid() {
    const svg = exportHeatmapGridSvg(getExportParams())
    const safeName = group.taxonomy_node_name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-')
    downloadSvg(svg, `heatmap-${safeName}.svg`)
  }

  function handleDownloadFull() {
    const svg = exportHeatmapFullSvg(getExportParams())
    const safeName = group.taxonomy_node_name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-')
    downloadSvg(svg, `heatmap-${safeName}-full.svg`)
  }

  // Sort risks by score descending — this ordering drives both the table and the numbering
  const sortedRisks = [...group.risks].sort((a, b) => {
    const scoreA = a.residual_likelihood * a.residual_max_impact
    const scoreB = b.residual_likelihood * b.residual_max_impact
    if (scoreB !== scoreA) return scoreB - scoreA
    return b.residual_likelihood - a.residual_likelihood
  })

  const displayedRisks = sortedRisks.slice(0, tableSize)

  // Build number map: risk ID → 1-based display number (only for table-listed risks)
  const riskNumbers = new Map<string, number>()
  for (let i = 0; i < displayedRisks.length; i++) {
    riskNumbers.set(displayedRisks[i]!.id, i + 1)
  }

  // Map ALL risks to PlottedRisk (numbered ones get labels, others get plain dots)
  const plottedRisks: PlottedRisk[] = group.risks.map((r) => ({
    id: r.id,
    shortRef: '',
    name: r.name,
    impact: r.residual_max_impact,
    likelihood: r.residual_likelihood,
    outsideAppetite: r.outside_appetite,
  }))

  const cols = gridConfig.likelihoodLevels.length
  const rows = gridConfig.impactLevels.length

  return (
    <div className="rounded-lg border">
      {/* Collapsible header */}
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30"
        onClick={onToggle}
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{group.taxonomy_breadcrumb}</p>
          <div className="flex items-baseline gap-2">
            <span className="text-base font-semibold">{group.taxonomy_node_name}</span>
            <span className="text-sm text-muted-foreground">
              {group.total_risk_count} {group.total_risk_count === 1 ? 'risk' : 'risks'}
              {isTruncated && (
                <>
                  {' · '}
                  <span className="font-medium text-amber-600">
                    showing top {group.risks.length} by score
                  </span>
                </>
              )}
              {outOfAppetiteCount > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-destructive">
                    {outOfAppetiteCount} outside appetite
                  </span>
                </>
              )}
              {outOfAppetiteCount === 0 && group.risks.length > 0 && (
                <>
                  {' · '}
                  <span className="text-muted-foreground">all within appetite</span>
                </>
              )}
            </span>
          </div>
          {group.statement && (
            <p className="mt-0.5 text-sm italic text-muted-foreground">{group.statement}</p>
          )}
        </div>
      </button>

      {/* Collapsible content */}
      {open && (
        <div className="border-t px-4 pb-4 pt-4">
          {/* Download buttons */}
          <div className="mb-3 flex justify-end gap-2">
            <button
              onClick={handleDownloadGrid}
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
            >
              <Download className="h-3 w-3" />
              Grid only
            </button>
            <button
              onClick={handleDownloadFull}
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
            >
              <Download className="h-3 w-3" />
              Grid + Table
            </button>
          </div>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="w-full max-w-xl shrink-0">
              <RiskGrid config={gridConfig} appetite={appetite}>
                <RiskHeatmapDots
                  risks={plottedRisks}
                  cols={cols}
                  rows={rows}
                  riskNumbers={riskNumbers}
                  onRiskClick={onRiskClick}
                />
              </RiskGrid>
            </div>

            {displayedRisks.length > 0 && (
              <TopRisksTable
                risks={displayedRisks}
                total={group.risks.length}
                tableSize={tableSize}
                onRiskClick={onRiskClick}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// --- Top risks table with numbered legend ---

interface TopRisksTableProps {
  risks: HeatmapRisk[]
  total: number
  tableSize: number
  onRiskClick: (riskId: string) => void
}

function TopRisksTable({ risks, total, tableSize, onRiskClick }: TopRisksTableProps) {
  return (
    <div className="w-full min-w-0">
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">
        Top risks by score
        {total > tableSize ? ` (showing ${risks.length} of ${total})` : ''}
      </h3>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="w-8 px-2 py-2 text-center font-medium">#</th>
              <th className="px-3 py-2 text-left font-medium">Risk</th>
              <th className="px-3 py-2 text-left font-medium">Owner</th>
              <th className="px-3 py-2 text-center font-medium">L</th>
              <th className="px-3 py-2 text-center font-medium">I</th>
              <th className="px-3 py-2 text-center font-medium">Score</th>
              <th className="w-8 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {risks.map((risk, idx) => {
              const score = risk.residual_likelihood * risk.residual_max_impact
              return (
                <tr
                  key={risk.id}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                  onClick={() => onRiskClick(risk.id)}
                >
                  <td className="px-2 py-2 text-center text-xs font-bold text-muted-foreground">
                    {idx + 1}
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2" title={risk.name}>
                    {risk.name}
                  </td>
                  <td
                    className="max-w-[120px] truncate px-3 py-2 text-muted-foreground"
                    title={risk.owner_display_name}
                  >
                    {risk.owner_display_name || '—'}
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums">
                    {risk.residual_likelihood}
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums">
                    {risk.residual_max_impact}
                  </td>
                  <td className="px-3 py-2 text-center font-medium tabular-nums">{score}</td>
                  <td className="px-2 py-2 text-center">
                    {risk.outside_appetite && (
                      <AlertTriangle className="inline h-3.5 w-3.5 text-destructive" />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
